import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const ChannelCallMenu = ({
  activeMeeting,
  isOrganizer,
  onStartDirectCall,
  onStartConnectMeeting,
  onJoinCall,
  onAddCallNotes,
  onCloseCall,
  onStartNewCall,
  headerActionButtonClass,
}) => {
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);
  const popupRef = useRef(null);
  const [popupPosition, setPopupPosition] = useState(null);

  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (
        menuRef.current
        && !menuRef.current.contains(e.target)
        && !popupRef.current?.contains(e.target)
      ) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  useEffect(() => {
    if (!open) {
      setPopupPosition(null);
      return undefined;
    }

    const updatePosition = () => {
      const rect = menuRef.current?.getBoundingClientRect();
      if (!rect) return;

      const menuWidth = 224;
      const viewportPadding = 8;
      setPopupPosition({
        top: rect.bottom + 6,
        left: Math.min(
          Math.max(viewportPadding, rect.right - menuWidth),
          window.innerWidth - menuWidth - viewportPadding,
        ),
      });
    };

    updatePosition();
    window.addEventListener('resize', updatePosition);
    window.addEventListener('scroll', updatePosition, true);
    return () => {
      window.removeEventListener('resize', updatePosition);
      window.removeEventListener('scroll', updatePosition, true);
    };
  }, [open]);

  const closeMenu = () => setOpen(false);

  const handleAction = (action) => {
    closeMenu();
    action?.();
  };

  const hasActiveCall = Boolean(activeMeeting);

  return (
    <div className="relative" ref={menuRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`${headerActionButtonClass} ${hasActiveCall ? 'text-indigo-600 dark:text-indigo-400' : ''}`}
        title={hasActiveCall ? 'Call in progress' : 'Start a call'}
        aria-label={hasActiveCall ? 'Call options' : 'Start a call'}
        aria-expanded={open}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        {hasActiveCall && (
          <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full ring-2 ring-white dark:ring-gray-800" />
        )}
      </button>

      {open && popupPosition && ReactDOM.createPortal(
        <div
          ref={popupRef}
          className="fixed w-56 rounded-xl border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 shadow-lg py-1 z-[9999]"
          style={popupPosition}
        >
          {!hasActiveCall ? (
            <>
              <button
                type="button"
                onClick={() => handleAction(onStartDirectCall)}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"
              >
                <span className="font-medium">Direct call</span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  In-app video · mic · screenshare
                </span>
              </button>
              <button
                type="button"
                onClick={() => handleAction(onStartConnectMeeting)}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 border-t border-gray-100 dark:border-gray-700"
              >
                <span className="font-medium">Connect meeting</span>
                <span className="block text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
                  Zoom / Meet / Teams link
                </span>
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => handleAction(onJoinCall)}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"
              >
                Join call
              </button>
              <button
                type="button"
                onClick={() => handleAction(onAddCallNotes)}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60"
              >
                Add call notes
              </button>
              {isOrganizer ? (
                <button
                  type="button"
                  onClick={() => handleAction(onCloseCall)}
                  className="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/30"
                >
                  Close call
                </button>
              ) : (
                <p className="px-4 py-2 text-xs text-gray-500 dark:text-gray-400 border-t border-gray-100 dark:border-gray-700 mt-1">
                  Only the organizer can close the call
                </p>
              )}
              <button
                type="button"
                onClick={() => handleAction(onStartNewCall)}
                className="w-full text-left px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700/60 border-t border-gray-100 dark:border-gray-700"
              >
                Start new call
              </button>
            </>
          )}
        </div>,
        document.body,
      )}
    </div>
  );
};

export default ChannelCallMenu;
